/**
 * PendingReviewPanel — 3-column detail view shown when an operator selects an
 * email card from InboxQueue. Displays the raw client email, an AI-extraction
 * placeholder, and a draft-reply placeholder. Replaces the generic PendingGrid
 * for the queue-click flow; W05 AI wiring will populate columns 2 and 3.
 */
import { useState } from 'react';
import type { BookingJob } from '@/lib/types/workflow';
import { WorkflowStatus } from '@/lib/types/workflow';
import { brand, fonts, styles, motion } from '@/lib/tokens';
import { formatSASTTime, formatSASTDate } from '@/lib/utils/date';
import { StatusBadge } from '@/components/ui/StatusBadge';
import ErrorBoundary from '@/components/ui/ErrorBoundary';
import { SkeletonPanel } from '@/components/ui/SkeletonCard';

interface PendingReviewPanelProps {
  job: BookingJob | null;
  onBack: () => void;
  isEnriching?: boolean;
}

export function PendingReviewPanel({ job, onBack, isEnriching = false }: PendingReviewPanelProps) {
  if (!job) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: fonts.mono,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: brand.monoDim,
        }}
      >
        No booking selected
      </div>
    );
  }

  const receivedLabel = job.receivedAt
    ? `${formatSASTDate(job.receivedAt)} · ${formatSASTTime(job.receivedAt)} SAST`
    : '';

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: '0 0 16px',
      }}
    >
      {/* Panel header */}
      <PanelHeader job={job} receivedLabel={receivedLabel} onBack={onBack} />

      {/* 3-column body */}
      <div
        style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '33fr 40fr 27fr',
          gap: 12,
          padding: '0 20px',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <ErrorBoundary>
          <ClientEmailColumn job={job} isEnriching={isEnriching} />
        </ErrorBoundary>
        <ErrorBoundary>
          <AIExtractionColumn job={job} isEnriching={isEnriching} />
        </ErrorBoundary>
        <ErrorBoundary>
          <DraftReplyColumn job={job} isEnriching={isEnriching} />
        </ErrorBoundary>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Panel header
// ---------------------------------------------------------------------------

function PanelHeader({
  job,
  receivedLabel,
  onBack,
}: {
  job: BookingJob;
  receivedLabel: string;
  onBack: () => void;
}) {
  const [btnHovered, setBtnHovered] = useState(false);

  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '10px 20px',
        borderBottom: `1px solid ${brand.hairlineSoft}`,
        marginBottom: 12,
      }}
    >
      {/* Back button */}
      <button
        onClick={onBack}
        onMouseEnter={() => setBtnHovered(true)}
        onMouseLeave={() => setBtnHovered(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          background: btnHovered ? brand.surface3 : 'transparent',
          border: `1px solid ${brand.hairline}`,
          borderRadius: 8,
          color: btnHovered ? brand.onSurface : brand.onSurfaceVariant,
          cursor: 'pointer',
          fontFamily: fonts.sans,
          fontSize: 12,
          padding: '4px 10px',
          transition: `background ${motion.default}, color ${motion.default}`,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
          chevron_left
        </span>
        Back to Queue
      </button>

      {/* Sender name */}
      <span
        style={{
          fontFamily: fonts.serif,
          fontSize: 24,
          fontWeight: 400,
          color: brand.onSurface,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {job.clientName}
      </span>

      {/* Status badge */}
      <StatusBadge status={WorkflowStatus.PENDING_REVIEW} />

      {/* Received time */}
      {receivedLabel && (
        <span style={{ ...styles.monoLabel, color: brand.monoDim, flexShrink: 0 }}>
          {receivedLabel}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column 1 — Client Email
// ---------------------------------------------------------------------------

function ClientEmailColumn({ job, isEnriching }: { job: BookingJob; isEnriching: boolean }) {
  if (isEnriching && !job.emailBody && job.gear.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
        <ColumnLabel>CLIENT EMAIL</ColumnLabel>
        <SkeletonPanel />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
      <ColumnLabel>CLIENT EMAIL</ColumnLabel>
      <div
        className="glass-surface card-border custom-scroll"
        style={{
          flex: 1,
          borderRadius: 12,
          padding: '20px 24px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {/* From */}
        <MetaRow label="From" value={job.clientEmail} />

        {/* Subject */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...styles.monoLabel, marginBottom: 4 }}>Subject</div>
          <div
            style={{
              fontFamily: fonts.serif,
              fontSize: 18,
              fontWeight: 400,
              color: brand.onSurface,
              lineHeight: 1.4,
            }}
          >
            {job.subject}
          </div>
        </div>

        {/* Received */}
        {job.receivedAt && (
          <MetaRow
            label="Received"
            value={`${formatSASTDate(job.receivedAt)} · ${formatSASTTime(job.receivedAt)} SAST`}
          />
        )}

        {/* Divider */}
        <div
          style={{
            height: 1,
            background: brand.hairlineSoft,
            margin: '16px 0',
            flexShrink: 0,
          }}
        />

        {/* Body */}
        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: 13,
            color: brand.onSurfaceVariant,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {job.emailBody || <em style={{ color: brand.monoDim }}>No body</em>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column 2 — AI Extraction placeholder
// ---------------------------------------------------------------------------

function AIExtractionColumn({ job, isEnriching }: { job: BookingJob; isEnriching: boolean }) {
  if (isEnriching && !job.emailBody && job.gear.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
        <ColumnLabel>AI EXTRACTION</ColumnLabel>
        <SkeletonPanel />
      </div>
    );
  }

  const isPending = job.gear.length === 0 && job.aiConfidence === 0;

  if (isPending) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
        <ColumnLabel>AI EXTRACTION</ColumnLabel>
        <div
          className="glass-surface custom-scroll"
          style={{
            flex: 1,
            borderRadius: 12,
            padding: '24px',
            border: `1px solid ${brand.accentSubtle}`,
            animation: 'pendingPulse 1s ease-in-out infinite',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
              textAlign: 'center',
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 32, color: brand.accent, animation: 'spin 1s linear infinite' }}
            >
              autorenew
            </span>
            <div
              style={{
                fontFamily: fonts.serif,
                fontSize: 20,
                fontWeight: 400,
                color: brand.onSurface,
              }}
            >
              AI Processing
            </div>
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: 12,
                color: brand.onSurfaceVariant,
              }}
            >
              Pending AI extraction
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
      <ColumnLabel>AI EXTRACTION</ColumnLabel>
      <div
        className="glass-surface card-border custom-scroll"
        style={{
          flex: 1,
          borderRadius: 12,
          padding: '20px 24px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        <MetaRow label="AI Confidence" value={`${Math.round(job.aiConfidence)}%`} />

        <div style={{ marginBottom: 12 }}>
          <div style={{ ...styles.monoLabel, marginBottom: 4 }}>Extracted Gear</div>
          {job.gear.length === 0 ? (
            <div style={{ fontFamily: fonts.sans, fontSize: 13, color: brand.onSurfaceVariant }}>
              <em style={{ color: brand.monoDim }}>No gear extracted</em>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {job.gear.map((item) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontFamily: fonts.sans,
                    fontSize: 13,
                    color: brand.onSurface,
                  }}
                >
                  <span>{item.name}</span>
                  <span style={{ color: brand.onSurfaceVariant }}>×{item.quantity}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            height: 1,
            background: brand.hairlineSoft,
            margin: '4px 0 16px',
            flexShrink: 0,
          }}
        />

        <MetaRow label="Location" value={job.location || '—'} />
        <MetaRow label="Rental Dates" value={job.rentalDates || '—'} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Column 3 — Draft Reply placeholder
// ---------------------------------------------------------------------------

function DraftReplyColumn({ job, isEnriching }: { job: BookingJob; isEnriching: boolean }) {
  if (isEnriching && !job.emailBody && job.gear.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
        <ColumnLabel>DRAFT REPLY</ColumnLabel>
        <SkeletonPanel />
      </div>
    );
  }

  if (!job.draftBody) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
        <ColumnLabel>DRAFT REPLY</ColumnLabel>
        <div
          className="glass-surface card-border"
          style={{
            flex: 1,
            borderRadius: 12,
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              textAlign: 'center',
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 28, color: brand.onSurfaceVariant }}
            >
              edit_note
            </span>
            <div
              style={{
                fontFamily: fonts.sans,
                fontSize: 12,
                color: brand.onSurfaceVariant,
              }}
            >
              Awaiting AI draft
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 8 }}>
      <ColumnLabel>DRAFT REPLY</ColumnLabel>
      <div
        className="glass-surface card-border custom-scroll"
        style={{
          flex: 1,
          borderRadius: 12,
          padding: '20px 24px',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        <div style={{ marginBottom: 12 }}>
          <div style={{ ...styles.monoLabel, marginBottom: 4 }}>Subject</div>
          <div
            style={{
              fontFamily: fonts.serif,
              fontSize: 18,
              fontWeight: 400,
              color: brand.onSurface,
              lineHeight: 1.4,
            }}
          >
            {job.draftSubject}
          </div>
        </div>

        <div
          style={{
            height: 1,
            background: brand.hairlineSoft,
            margin: '16px 0',
            flexShrink: 0,
          }}
        />

        <div
          style={{
            fontFamily: fonts.sans,
            fontSize: 13,
            color: brand.onSurfaceVariant,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {job.draftBody}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function ColumnLabel({ children }: { children: string }) {
  return (
    <div style={{ ...styles.monoLabel, color: brand.accent, paddingLeft: 2, flexShrink: 0 }}>
      {children}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...styles.monoLabel, marginBottom: 2 }}>{label}</div>
      <div
        style={{
          fontFamily: fonts.mono,
          fontSize: 11,
          color: brand.onSurfaceVariant,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </div>
    </div>
  );
}
